import { getCurrentUserDetail } from "@payping/redux/actions";
import {
  Button,
  Checkbox,
  Col,
  Drawer,
  Flex,
  Form,
  Row,
  Skeleton,
  Space,
  Typography,
  useMessagedAsyncFn,
} from "@payping/web-components";
import React, { useEffect } from "react";

import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { userApis } from "@payping/api";
import { USER_CLAIMS_LIST, useUserClaim } from "@/utils/useUserClaims";
import { useImmerAtom } from "jotai-immer";
import { coreAtom } from "@/core/state/root";
import { URLS } from "@payping/utilities-shared/consts";
import { Close } from "@payping/icons";
import { useNavigate } from "react-router-dom";
import { DashboardRoutes } from "@/router";

const MobileShopSettings: React.FC = () => {
  const [, setCoreState] = useImmerAtom(coreAtom);

  const { settings } = DashboardRoutes;
  const dispatch = useAppDispatch();
  const userDetail = useAppSelector((state) => state.user);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { t } = useAppTranslation();
  const { hasPermission } = useUserClaim([USER_CLAIMS_LIST.shopService]);

  const [{ loading }, onFinish] = useMessagedAsyncFn(
    async (value) => {
      if (value.shopSetting === true) {
        await userApis.requestShopEnamadWithCancellationTokenGet();
        await dispatch(getCurrentUserDetail());
      }
    },
    [],
    undefined,
  );

  useEffect(() => {
    form.setFieldsValue({
      shopSetting: userDetail.isShopEnamadRequested,
    });
  }, [form, userDetail]);

  return (
    <Drawer
      open={true}
      placement={"bottom"}
      height={"100%"}
      title={t("settings.shop.title")}
      closeIcon={false}
      extra={
        <Button
          type={"text"}
          icon={<Close width={14} height={14} />}
          onClick={() => {
            navigate(settings.mRoot.url);
          }}
        />
      }
      footer={
        <Form.Item noStyle dependencies={["shopSetting"]}>
          {({ getFieldValue }) => {
            const active = getFieldValue("shopSetting");

            return (
              <Button
                type={"primary"}
                className="tw-my-4"
                block
                loading={loading}
                disabled={!active || userDetail.isShopEnamadRequested}
                htmlType={hasPermission ? "submit" : "button"}
                onClick={() =>
                  hasPermission
                    ? undefined
                    : setCoreState((draft) => {
                        draft.upgradeUserPanel.toStandard.service = "onlineShop";
                        draft.upgradeUserPanel.toStandard.showModal = true;
                      })
                }
              >
                {t("settings.shop.activate")}
              </Button>
            );
          }}
        </Form.Item>
      }
    >
      <Flex vertical gap={"large"}>
        <Typography.Title level={4} className="!tw-m-0">
          {t("settings.shop.title")}
        </Typography.Title>
        <Row className={"tw-w-full"}>
          <Col lg={12} xl={16} className={"tw-flex tw-items-center"}>
            <Form form={form} onFinish={hasPermission ? onFinish : undefined}>
              <Form.Item>
                <Space
                  direction={"vertical"}
                  size={"middle"}
                  className={"tw-flex tw-w-full tw-items-center tw-text-center"}
                >
                  <img
                    className="tw-h-[100px] tw-w-[100px] tw-object-cover"
                    src={`${URLS.STORAGE_URL}/dashboard-app/storefront.png`}
                  />
                  <Typography.Title className={"!tw-mb-0"} level={4}>
                    {t("settings.shop.shopActivation")}
                  </Typography.Title>
                  <Typography.Text>{t("settings.shop.description")}</Typography.Text>
                </Space>
              </Form.Item>
              <Form.Item name={"shopSetting"} valuePropName={"checked"}>
                {loading ? (
                  <Skeleton.Input />
                ) : (
                  <Checkbox disabled={userDetail.isShopEnamadRequested}>{t("settings.shop.successShowInfo")}</Checkbox>
                )}
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Flex>
    </Drawer>
  );
};

export default MobileShopSettings;
